import {
  catalogApi,
  fetchPage,
  type CatalogRow,
  type ListParams,
  type PageResponse,
} from './collections';
import { apiFetch } from './http';

/**
 * The generic approvals engine (`AM-133`, `07 §4.10`): `approval_requests`/`approval_steps` over
 * an entity of origin the module never depends on (`entityType`/`entityId`, `CONV-61`), plus the
 * tenant-authored `approval_rules` that open them. Mirrors `notifications.ts` in shape; the two
 * request shapes (list vs. detail) are genuinely different here - the list never carries the step
 * roster, unlike `NotificationRule` - so callers that need the steps always read the detail.
 */

export const APPROVAL_ENTITY_TYPES = ['request', 'ticket', 'contract', 'invoice'] as const;
export type ApprovalEntityType = (typeof APPROVAL_ENTITY_TYPES)[number];

export const APPROVAL_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'cancelled',
  'expired',
] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export const APPROVAL_STEP_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'skipped',
  'expired',
] as const;
export type ApprovalStepStatus = (typeof APPROVAL_STEP_STATUSES)[number];

export const APPROVER_TYPES = ['user', 'role', 'team'] as const;
export type ApproverType = (typeof APPROVER_TYPES)[number];

/** One row of `GET /approval-requests` or `GET /me/approvals` (`07 §4.10`). */
export interface ApprovalRequestSummary {
  id: string;
  entityType: ApprovalEntityType;
  entityId: string;
  entityReference?: string | null;
  approvalType: string;
  status: ApprovalStatus;
  currentStepOrder: number | null;
  requestedByUserId?: string | null;
  requestedByDisplayName?: string | null;
  requestedAt: string;
  dueAt?: string | null;
  completedAt?: string | null;
  outcomeComments?: string | null;
  eTag: string;
}

/** One step of an `ApprovalRequestDetail` (`09 §17`): whichever of user/role/team names the approver. */
export interface ApprovalStepEntry {
  id: string;
  stepOrder: number;
  approverType: ApproverType;
  approverId: string;
  approverDisplayName?: string | null;
  status: ApprovalStepStatus;
  isMandatory: boolean;
  respondedByUserId?: string | null;
  respondedByDisplayName?: string | null;
  respondedAt?: string | null;
  comments?: string | null;
}

/** `GET /approval-requests/{id}`: the summary plus its steps, ordered. */
export interface ApprovalRequestDetail extends ApprovalRequestSummary {
  steps: ApprovalStepEntry[];
}

/**
 * What an approval is about, in one line: the number and the title of the request or the ticket,
 * and the person it is for. Somebody of the customer's side, never the bench.
 */
export interface ApprovalSubjectSummary {
  number?: string | null;
  title: string;
  requesterName?: string | null;
}

/** What the approver of a Change reads about it besides its description. */
export interface ApprovalSubjectChange {
  changeType: string;
  riskLevel?: string | null;
  reason?: string | null;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
}

/**
 * What an approval is about, as its approver reads it before deciding: the request or the ticket
 * with its description and the data of its service, and nothing of the bench.
 */
export interface ApprovalSubject extends ApprovalSubjectSummary {
  description?: string | null;
  organizationName?: string | null;
  serviceAreaName?: string | null;
  serviceName?: string | null;
  serviceOfferingName?: string | null;
  categoryName?: string | null;
  locationName?: string | null;
  /** `yyyy-MM-dd`: the day the requester asked for it, when they said. */
  requestedDate?: string | null;
  createdAt: string;
  change?: ApprovalSubjectChange | null;
}

/**
 * `GET /me/approvals`: the current step the caller may decide, alongside the request it belongs to
 * and what it is about; `GET /me/approvals/decided` answers the same with the step already decided.
 */
export interface MyApprovalEntry {
  step: ApprovalStepEntry;
  request: ApprovalRequestSummary;
  subject?: ApprovalSubjectSummary | null;
}

/** The caller's own decision on a step of an approval. */
export interface MyApprovalDecision {
  stepId: string;
  status: ApprovalStepStatus;
  respondedAt?: string | null;
  comments?: string | null;
}

/**
 * `GET /me/approvals/{id}`: one approval as one of its approvers reads it. `decidableStepId` is the
 * step they may approve or reject now; `decision`, what they already decided. It names nobody of
 * the bench: the person it is for travels in `subject`.
 */
export interface MyApprovalDetail {
  id: string;
  entityType: ApprovalEntityType;
  entityId: string;
  entityReference?: string | null;
  approvalType: string;
  status: ApprovalStatus;
  requestedAt: string;
  dueAt?: string | null;
  completedAt?: string | null;
  outcomeComments?: string | null;
  decidableStepId?: string | null;
  decision?: MyApprovalDecision | null;
  subject?: ApprovalSubject | null;
}

/** One entry of an `approval_rules.steps_definition` (`05g §6.1`, `AM-133 point 3`). */
export interface ApprovalStepDefinitionInput {
  stepOrder: number;
  approverType: ApproverType;
  approverId: string;
  isMandatory: boolean;
}

export interface CreateApprovalRequestInput {
  entityType: ApprovalEntityType;
  entityId: string;
  approvalType: string;
  stepsDefinition?: ApprovalStepDefinitionInput[];
  dueAt?: string;
}

/** One row of `GET /approval-rules` (`admin.approvals.manage`); the condition and steps live only on the detail. */
export interface ApprovalRuleSummary extends CatalogRow {
  name: string;
  description?: string | null;
  entityType: ApprovalEntityType;
  approvalType: string;
  dueAfterHours?: number | null;
  isActive: boolean;
  priorityOrder: number;
}

/** `GET/POST/PATCH /approval-rules/{id}`: the summary plus the two documents `SEC-06` evaluates. */
export interface ApprovalRuleDetail extends ApprovalRuleSummary {
  /** `{}` or `{"schema_version": 1, ...RuleCondition AST}` (`AM-133 point 3`); opaque to the console. */
  condition: unknown;
  stepsDefinition: ApprovalStepDefinitionInput[];
  createdAt: string;
}

export const approvalRequestsApi = {
  list: (params: ListParams, signal?: AbortSignal): Promise<PageResponse<ApprovalRequestSummary>> =>
    fetchPage<ApprovalRequestSummary>('/api/v1/approval-requests', params, signal),
  get: (id: string, signal?: AbortSignal): Promise<ApprovalRequestDetail> =>
    apiFetch<ApprovalRequestDetail>(
      `/api/v1/approval-requests/${id}`,
      signal === undefined ? {} : { signal },
    ),
  create: (body: CreateApprovalRequestInput): Promise<ApprovalRequestDetail> =>
    apiFetch<ApprovalRequestDetail>('/api/v1/approval-requests', { method: 'POST', body }),
  cancel: (id: string): Promise<ApprovalRequestDetail> =>
    apiFetch<ApprovalRequestDetail>(`/api/v1/approval-requests/${id}/cancel`, { method: 'POST' }),
};

export const approvalStepsApi = {
  approve: (id: string, comments?: string): Promise<ApprovalRequestDetail> =>
    apiFetch<ApprovalRequestDetail>(`/api/v1/approval-steps/${id}/approve`, {
      method: 'POST',
      body: { comments },
    }),
  /** `ck_approval_steps_reject_needs_comment`: the server refuses an empty comment with `422 comment_required`. */
  reject: (id: string, comments: string): Promise<ApprovalRequestDetail> =>
    apiFetch<ApprovalRequestDetail>(`/api/v1/approval-steps/${id}/reject`, {
      method: 'POST',
      body: { comments },
    }),
};

/**
 * `GET /me/approvals` (no filters, no cursor: the tenant's own bounded roster of the caller's pending
 * decisions), what the caller already decided, and one approval as its approver reads it (the
 * portal's "Mis aprobaciones", `approvals.act`).
 */
export const myApprovalsApi = {
  list: (signal?: AbortSignal): Promise<PageResponse<MyApprovalEntry>> =>
    fetchPage<MyApprovalEntry>('/api/v1/me/approvals', {}, signal),
  decided: (signal?: AbortSignal): Promise<PageResponse<MyApprovalEntry>> =>
    fetchPage<MyApprovalEntry>('/api/v1/me/approvals/decided', {}, signal),
  get: (id: string, signal?: AbortSignal): Promise<MyApprovalDetail> =>
    apiFetch<MyApprovalDetail>(
      `/api/v1/me/approvals/${id}`,
      signal === undefined ? {} : { signal },
    ),
};

/** `GET/POST/PATCH /approval-rules`; no `DELETE` - a rule is retired with `PATCH {"isActive": false}` (`AM-133 point 11`). */
export const approvalRulesApi = catalogApi<ApprovalRuleSummary>('/api/v1/approval-rules');

export function getApprovalRule(id: string, signal?: AbortSignal): Promise<ApprovalRuleDetail> {
  return apiFetch<ApprovalRuleDetail>(
    `/api/v1/approval-rules/${id}`,
    signal === undefined ? {} : { signal },
  );
}
